import React, { useMemo, useState } from "react";
import { colors2, TabBarV2 } from "oolib";
import { UI_CAPTION } from "oolib/dist/v2/components/Typo2";

import { BarChart } from "@/components/dataviz/BarChart";
import { useAggrMetricsAcrossDimensions } from "../../../WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { WidgetEmptyState, WidgetErrorState, WidgetLoadingState } from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { ASSESSMENTS, CENTRE_ROLLUP_PATH, DATASETS, isMadTenant } from "../madSettings";

export { CentreGradesConfigForm as ConfigForm } from "./CentreGradesConfigForm";

/**
 * MAD - Assessment Grade by Centre — for one assessment template at a time,
 * a grouped bar chart: one group per MAD centre, and inside it one bar per
 * assessment (Baseline → Endline) whose height is the average assessment
 * grade across the centre's children for that assessment. An assessment no
 * child at the centre has had yet draws no bar.
 *
 * One aggregation call per template (the endpoint every chart widget uses):
 *   dimension 1  the MAD centre — a rollup from the doc's volunteer tag to
 *                the volunteer's own centre tag (volunteers → mADCentres)
 *   dimension 2  the assessment radio
 *   metrics      count, and avg over the recorded assessment grade
 * The service sums metrics at the parent level of a multi-dimension result,
 * so the centre row's own average is meaningless; only the leaves (centre ×
 * assessment) are read.
 *
 * Only templates that record an assessment grade are offered — Life Skills
 * records none, so it is not a tab here.
 */
const AVERAGE = "averageLevel";

const DATASET_OPTIONS = Object.entries(DATASETS)
  .filter(([, d]) => d.levelValuePath)
  .map(([value, d]) => ({ value, display: d.label }));

const VALUE_PATH = ASSESSMENTS.map((_, i) => `children.${i}.count`);
const TOOLTIP_LABELS_PATH = ASSESSMENTS.map((_, i) => `children.${i}.display`);

/**
 * Service rows → chart rows: one per centre, in name order, with a child per
 * assessment in cycle order. `count` carries the AVERAGE (the chart reads
 * `children.i.count`); an assessment the centre has no docs for gets a child
 * with no count, which the chart draws as no bar, under the right label.
 */
const toChartRows = (rows) =>
  [...(rows || [])]
    .sort((a, b) => String(a.display || a.value).localeCompare(String(b.display || b.value)))
    .map((centre) => ({
      value: centre.value,
      display: centre.display || String(centre.value),
      children: ASSESSMENTS.map((a) => {
        const leaf = (centre.children || []).find((c) => String(c.value) === a.value);
        const average = typeof leaf?.[AVERAGE] === "number" ? Math.round(leaf[AVERAGE] * 10) / 10 : undefined;
        return { value: a.value, display: a.display, ...(average !== undefined ? { count: average, children: leaf.count } : {}) };
      }),
    }));

export const Component = ({ id, widgetIdx, activeFilters = [], presetActiveFilters = [], widgetFilters = [], style = {} }) => {
  const [contentType, setContentType] = useState(DATASET_OPTIONS[0].value);
  const ready = isMadTenant();
  const dataset = DATASETS[contentType];

  // the report's filters plus the widget's own, the way every widget combines them
  const filters = useMemo(
    () => [...activeFilters, ...presetActiveFilters, ...widgetFilters],
    [activeFilters, presetActiveFilters, widgetFilters]
  );

  const aggregation = useAggrMetricsAcrossDimensions({
    contentType,
    activeFilters: filters,
    dimensions: [
      { rollupType: "tagType", path: CENTRE_ROLLUP_PATH },
      dataset.assessmentValuePath.replace(/\.value$/, ""),
    ],
    metrics: [
      { type: "count", name: "count" },
      { type: "avg", name: AVERAGE, field: dataset.levelValuePath },
    ],
    queryOptions: { enabled: ready },
  });

  const rows = useMemo(() => toChartRows(aggregation.data), [aggregation.data]);

  const status = !ready ? "success" : aggregation.status;
  useUpdateLoadedWidgetsTracker({ status, widgetIdx, hasData: rows.length > 0, widgetId: id });

  if (!ready) return <WidgetEmptyState display="This widget is not set up for this platform" />;

  return (
    <div>
      <div style={{ padding: "1.2rem 1.6rem", background: colors2.white, borderTop: `1px solid ${colors2.grey10}`, borderBottom: `1px solid ${colors2.grey10}` }}>
        <TabBarV2
          id={`${id}:template`}
          options={DATASET_OPTIONS}
          value={contentType}
          onChange={(_id, value) => setContentType(value)}
          saveValueAsString
          style={{ backgroundColor: "transparent" }}
        />
      </div>
      <div style={{ padding: "1.6rem" }}>
        {status === "loading" ? (
          <WidgetLoadingState />
        ) : status === "error" ? (
          <WidgetErrorState error={aggregation.error} />
        ) : rows.length === 0 ? (
          <WidgetEmptyState display="No assessments with a grade match" />
        ) : (
          <>
            <UI_CAPTION style={{ color: colors2.grey60, display: "block", marginBottom: "0.8rem" }}>
              Average assessment grade per centre, one bar per assessment
            </UI_CAPTION>
            <BarChart
              data={rows}
              valuePath={VALUE_PATH}
              tooltipLabelsPath={TOOLTIP_LABELS_PATH}
              labelPath="display"
              breakdownDisplayType="grouped"
              breakdownCategoryName="Assessment"
              // the bars are averages, not counts: no percent-of-total reading
              showPercent={false}
              // centres in name order, assessments in cycle order — not by value
              preserveOrder
              colorIdx={widgetIdx % 4}
              style={style}
            />
          </>
        )}
      </div>
    </div>
  );
};

// MAD's widget, on MAD only — no other tenant sees it in the picker.
export const optionDef = {
  display: "MAD - Assessment Grade by Centre",
  icon: "ChartBarHorizontal",
  desc: "Per MAD centre, the average assessment grade for each assessment, Baseline to Endline.",
  isAvailableToAdd: isMadTenant,
};

export const defaultConfig = { props: {} };

// The templates this widget reads, for the report's filter sidebar (see the
// container's processRawConfigFn).
export const processRawConfigFn = (widgetConfig) => ({
  ...widgetConfig,
  props: { ...widgetConfig.props, contentTypes: DATASET_OPTIONS.map((d) => d.value) },
});
